import { type MedalTimes, medalFor } from './medals';
import type { Split } from './RunTimer';
import { formatTicks } from './RunTimer';
import type { MapRecord } from './records';

/**
 * Multi-line end-of-run summary for the banner: time, verdict vs the previous
 * PB, and per-checkpoint splits with deltas against the PB's splits.
 * `previous` is the record BEFORE this run was submitted.
 * `medals` is optional; when provided and a medal is earned on a valid run, a
 * `medal: TIER` line is inserted after the verdict line.
 */
export function buildRunSummary(
  ticks: number,
  splits: Split[],
  valid: boolean,
  previous: MapRecord | undefined,
  medals?: MedalTimes,
): string {
  const time = formatTicks(ticks);
  if (!valid) {
    return `${time} — invalid run (missed checkpoint)\nR to retry`;
  }

  const verdict =
    previous === undefined
      ? 'first completion!'
      : ticks < previous.pbTicks
        ? `PB!  −${formatTicks(previous.pbTicks - ticks)}`
        : `+${formatTicks(ticks - previous.pbTicks)} vs PB ${formatTicks(previous.pbTicks)}`;

  const lines = [`${time} — ${verdict}`];

  const medal = medalFor(ticks, medals);
  if (medal !== null) {
    lines.push(`medal: ${medal.toUpperCase()}`);
  }

  for (const split of splits) {
    const pbSplit = previous?.splits[split.checkpoint];
    const delta =
      pbSplit === undefined
        ? ''
        : split.ticks <= pbSplit
          ? `  (−${formatTicks(pbSplit - split.ticks)})`
          : `  (+${formatTicks(split.ticks - pbSplit)})`;
    lines.push(`CP${split.checkpoint + 1}  ${formatTicks(split.ticks)}${delta}`);
  }
  lines.push('R to go again · Esc for menu');
  return lines.join('\n');
}
